import { useLazyQuery } from "@apollo/client";
import useQuery from "../other/useQuery.other.hook";
import { useEffect, useState } from "react";
import useAbortController from "../other/useAbortController.other.hook";

// GRAPHQL
import { GET_TICKETS, GET_TICKETS_BY_CUSTOMER_ID } from "../../graphql/queries/tickets/tickets.queries";

// UTILS
import convertToyyyyMMddThhmm from "../../utils/conversions/dates/yyyyMMddThhmm.date.conversion.util";

const DEFAULT_QUERY = {
  search: "",
  page: 1,
  limit: 100,
  sort: "-number",
};

const DEFAULT_INPUT = {
  statuses: ["Ready for shipment", "Uploaded to Shipstore"],
};

const useGetManyTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [ticketInput, setTicketInput] = useState(DEFAULT_INPUT);
  const [getTicketQuery, { loading, error, data }] = useLazyQuery(GET_TICKETS, {
    fetchPolicy: "no-cache",
  });

  // Using the abort controller hook
  const { createAbortController, signal } = useAbortController();

  const {
    query,
    setQuery,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
  } = useQuery({ defaultQuery: DEFAULT_QUERY });

  const getTickets = async () => {
    const controller = createAbortController(); // Create a new controller for each request

    try {
      const res = await getTicketQuery({
        variables: { query, ticket: ticketInput },
        context: {
          fetchOptions: {
            signal: controller.signal, // Pass the signal from the controller
          },
        },
      });
      if (res?.data?.tickets?.tickets)
        setTickets(
          res.data.tickets.tickets.map((ticket) => ({
            ...ticket,
            created_at: convertToyyyyMMddThhmm(ticket?.created_at),
            updated_at: convertToyyyyMMddThhmm(ticket?.updated_at),
            resolved_at: convertToyyyyMMddThhmm(ticket?.resolved_at),
            received_at: convertToyyyyMMddThhmm(ticket?.received_at),
          }))
        );
      if (res?.data?.tickets?.metaData) setMetaData(res.data.tickets.metaData);
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Request canceled:", error.message);
      } else {
        console.error(`Error getting ticket: ${error?.message}`);
        alert(`Error getting tickets: ${error?.message}`);
      }
    }
  };

  useEffect(() => {
    getTickets();
  }, [query, ticketInput]);

  return {
    getTickets,
    tickets,
    setTickets,
    metaData,
    setMetaData,
    ticketInput,
    setTicketInput,
    query,
    setQuery,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
    loading,
    error,
  };
};

const useGetTicketsByCustomerId = (customerId) => {
  const [tickets, setTickets] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [getTicketQuery, { loading, error, data }] = useLazyQuery(GET_TICKETS_BY_CUSTOMER_ID, {
    fetchPolicy: "no-cache",
  });

  // Using the abort controller hook
  const { createAbortController } = useAbortController();

  const {
    query,
    setQuery,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
  } = useQuery({ defaultQuery: DEFAULT_QUERY });

  const getTickets = async () => {
    const controller = createAbortController(); // Create a new controller for each request

    try {
      const res = await getTicketQuery({
        variables: { customerId, query },
        context: {
          fetchOptions: {
            signal: controller.signal, // Pass the signal from the controller
          },
        },
      });
      if (res?.data?.tickets?.tickets)
        setTickets(
          res.data.tickets.tickets.map((ticket) => ({
            ...ticket,
            created_at: convertToyyyyMMddThhmm(ticket?.created_at),
            updated_at: convertToyyyyMMddThhmm(ticket?.updated_at),
            resolved_at: convertToyyyyMMddThhmm(ticket?.resolved_at),
            received_at: convertToyyyyMMddThhmm(ticket?.received_at),
          }))
        );
      if (res?.data?.tickets?.metaData) setMetaData(res.data.tickets.metaData);
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Request canceled:", error.message);
      } else {
        console.error(`Error getting ticket: ${error?.message}`);
        alert(`Error getting tickets: ${error?.message}`);
      }
    }
  };

  useEffect(() => {
    if (customerId) getTickets();
  }, [customerId, query]);

  return {
    getTickets,
    tickets,
    setTickets,
    metaData,
    setMetaData,
    query,
    setQuery,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
    loading,
    error,
  };
};

export { useGetTicketsByCustomerId };
export default useGetManyTickets;
